<template>
    <v-container class="py-5 grey lighten-2" fluid>
        <v-row justify="center" align="center">
            <v-col cols="12" class="" justify="center" align="center">
                <v-card class="justify-center" elevation="2" max-width="450px">
                    <v-card-title class="grey lighten-3">Generar Orden de Producción</v-card-title>
                    <v-card-text>
                        <v-form ref="form" v-model="valid" lazy-validation>
                            <v-row align-content="center" justify="center" align="center">
                                <v-col cols="12">
                                    <v-autocomplete v-model="form.vendedora" :rules="vendedoraRules" v-if="personal"
                                        :items="personal" label="Vendedor"></v-autocomplete>
                                </v-col>
                                <v-col cols="12">
                                    <v-autocomplete
                                        v-if="mostrarSelectorProducto"
                                        dense
                                        v-model="producto"
                                        :items="searchResults"
                                        :loading="isSearching"
                                        :search-input.sync="productSearch"
                                        label="Producto"
                                        placeholder="Buscar producto (mín. 4 caracteres)"
                                        item-text="rename"
                                        item-value="id"
                                        return-object
                                        no-filter
                                        hide-no-data
                                        @change="onProductSelected"
                                    >
                                        <template v-slot:item="{ item }">
                                            <span class="caption">{{ item.rename }}</span>
                                        </template>
                                    </v-autocomplete>
                                    <v-text-field
                                        v-else-if="productoNombre"
                                        :value="productoNombre"
                                        label="Producto"
                                        readonly
                                        dense
                                        append-icon="mdi-package-variant"
                                    />
                                </v-col>
                                <v-col cols="12">
                                    <v-text-field
                                        v-model="form.color"
                                        label="Color"
                                        placeholder="Ej: Blanco, Negro, Gris..."
                                        dense
                                    />
                                </v-col>
                                <v-col cols="12">
                                    <v-text-field
                                        v-model="form.medida"
                                        label="Medida"
                                        placeholder="Ej: 140 x 80 x 45"
                                        dense
                                    />
                                </v-col>
                                <v-col cols="12">
                                    <v-text-field
                                        v-model.number="form.cantidad"
                                        label="Cantidad"
                                        type="number"
                                        min="1"
                                        dense
                                    />
                                </v-col>
                                <v-col cols="12">
                                    <v-autocomplete v-model="form.personalCargo" :rules="personalCargoRules"
                                        v-if="personal" :items="personal" label="Personal a Cargo"></v-autocomplete>
                                </v-col>
                                <v-col cols="12">
                                    <v-textarea v-model="form.observaciones" name="input-7-1" label="Observaciones"
                                        value=""></v-textarea>
                                </v-col>
                                <v-col cols="12" text-center>
                                    <v-btn block :disabled="!valid" :loading="loading" color="success" class="mr-4"
                                        @click="validate"> Generar </v-btn>
                                </v-col>
                            </v-row>
                        </v-form>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>
<script>
import { mapState } from 'vuex'
import { debounce } from 'lodash'
import { buildOrdenProduccionPayload, parsearProductosVenta, debeMostrarSelectorProducto } from '@/utils/ordenProduccionPayload'

function parseProductoDescripcion(desc) {
    let color = null
    let medida = null
    if (!desc) return { color, medida }

    // Buscar Medida: con o sin corchetes, con o sin parentesis
    const medidaMatch = desc.match(/Medida\s*:\s*\(?\[?([^\]\)]+)\]?\)?/i)
    if (medidaMatch) medida = medidaMatch[1].trim()

    // Buscar Color: hasta el siguiente campo conocido o fin
    const colorMatch = desc.match(/Color\s*:\s*(.+?)(?:\s+(?:Forma|Medida|Sistema|Material|Marca|\|)\s*:|\s*$)/i)
    if (colorMatch) color = colorMatch[1].trim()

    return { color, medida }
}

export default {
    computed: {
        ...mapState('users', ['user', 'personal', 'listaUser']),
        ...mapState('ventas', ['datos']),
        productoNombre() {
            if (this.producto) return this.producto.description || this.producto.name || ''
            if (this.dataProducto) return this.dataProducto.description || this.dataProducto.name || ''
            return ''
        },
        mostrarSelectorProducto() {
            return debeMostrarSelectorProducto({
                checkbox: this.checkbox,
                productoNombre: this.productoNombre
            })
        }
    },
    data() {
        return {
            dataVenta: '',
            dataProducto: '',
            valid: true,
            obtenerVenta: null,
            producto: null,
            loading: false,
            checkbox: false,
            productSearch: '',
            searchResults: [],
            isSearching: false,
            vendedoraRules: [(v) => !!v || 'Vendedora is required'],
            personalCargoRules: [(v) => !!v || 'Personal a cargo is required'],
            form: {
                vendedora: '',
                observaciones: '',
                personalCargo: '',
                color: '',
                medida: '',
                cantidad: 1
            }
        }
    },
    watch: {
        productSearch: debounce(function(val) {
            // Si ya hay un producto seleccionado, no relanzar búsqueda
            if (this.producto && this.producto.id) return
            if (!val || val.trim().length < 4) {
                this.searchResults = []
                return
            }
            this.performProductSearch(val)
        }, 500)
    },
    async mounted() {
        await this.$store.dispatch('users/getPersonas')

        // La OP se puede crear sin venta (STOCK): ahi el API responde data:null
        // y antes esto cortaba el mounted. Como consecuencia `checkbox` nunca
        // pasaba a true y el buscador de productos no se renderizaba, asi que
        // no habia forma de elegir producto.
        let obtenerVenta = null
        try {
            obtenerVenta = await this.$store.dispatch('ventas/obtenerVenta', {
                id: this.$route.query.idVenta
            })
        } catch (error) {
            console.warn('obtenerVenta no disponible, se usa el buscador de productos:', error?.message)
        }

        // Usar el idColaborador de la venta para obtener el nombre de la vendedora
        this.form.vendedora = this.listaUser.find((x) => x.id == obtenerVenta?.idColaborador)?.nombres || ''
        const productosVenta = parsearProductosVenta(obtenerVenta?.productos)
        this.obtenerVenta = productosVenta

        let obtenerProducto = null
        if (this.$route.query.idProducto && productosVenta) {
            obtenerProducto = productosVenta.find((x) => x.id === Number(this.$route.query.idProducto)) || null
        }

        if (!obtenerProducto) {
            // No se encontró el producto por query params: mostrar buscador
            this.checkbox = true
        }

        // Parsear color y medida de la descripción del producto
        if (obtenerProducto) {
            const desc = obtenerProducto.description || ''
            const parsed = parseProductoDescripcion(desc)
            this.form.color = obtenerProducto.color || parsed.color || ''
            this.form.medida = obtenerProducto.medida || parsed.medida || ''
            this.form.cantidad = obtenerProducto.unidades || 1
        }

        this.dataVenta = obtenerVenta
        this.dataProducto = obtenerProducto
    },
    methods: {
        async performProductSearch(searchText) {
            this.isSearching = true
            try {
                const results = await this.$store.dispatch('productos/searchProductos', {
                    text: searchText,
                    idSede: this.user.idSede,
                    showInternals: true
                })
                this.searchResults = results.map((p) => ({
                    ...p,
                    rename: `${p.name || p.description} -|- ${p.internal_id}`,
                    price: p.sale_unit_price
                }))
            } catch (error) {
                console.error('Product search error:', error)
                this.searchResults = []
            } finally {
                this.isSearching = false
            }
        },

        onProductSelected(product) {
            if (!product) return
            this.producto = {
                ...product,
                description: product.name || product.description,
                price: product.sale_unit_price
            }
            // Autocompletar color, medida y cantidad desde la descripción
            const desc = product.description || product.name || ''
            const parsed = parseProductoDescripcion(desc)
            this.form.color = product.color || parsed.color || ''
            this.form.medida = product.medida || parsed.medida || ''
            this.form.cantidad = product.unidades || 1
        },

        avisar(text) {
            this.$store.commit('mainUI/OPEN_MODAL', {
                state: true,
                text,
                color: 'red',
                close: true
            })
        },

        async validate() {
            const validacion = await this.$refs.form.validate()
            var today = new Date()
            var dd = today.getDate()
            var mm = today.getMonth() + 1
            var yyyy = today.getFullYear()
            if (dd < 10) dd = '0' + dd
            if (mm < 10) mm = '0' + mm
            today = yyyy + '-' + mm + '-' + dd

            const ordenPedido = this.$route.query.idVenta
            if (validacion) {
                const payload = buildOrdenProduccionPayload({
                    vendedor: this.form.vendedora,
                    personalCargo: this.form.personalCargo,
                    fechaEmision: today,
                    ordenPedido,
                    producto: this.producto,
                    dataProducto: this.dataProducto,
                    color: this.form.color,
                    medida: this.form.medida,
                    material: 'FIBRA DE VIDRIO',
                    observaciones: this.form.observaciones,
                    cantidad: this.form.cantidad
                })

                // Sin producto la OP no se puede crear: antes se enviaba
                // `producto: undefined`, axios borraba la clave del JSON y el
                // API respondia 500 (columna NOT NULL sin default).
                if (!payload) {
                    this.avisar('Selecciona un producto antes de generar la orden de produccion.')
                    return
                }

                this.loading = true
                let OrdenDeProduccion
                try {
                    OrdenDeProduccion = await this.$store.dispatch('ventas/crearOrdenPedido', payload)
                } catch (error) {
                    this.loading = false
                    this.avisar(error?.response?.data?.message || 'No se pudo generar la orden de produccion.')
                    return
                }
                this.$store.dispatch('reporte/reporteOrdenProduccion', {
                    id: OrdenDeProduccion.data.id,
                    annio: OrdenDeProduccion.data.fechaEmision.split('-')[0],
                    vendedora: this.form.vendedora,
                    fechaEmision: OrdenDeProduccion.data.fechaEmision,
                    ordenPedido: OrdenDeProduccion.data.ordenPedido,
                    personalCargo: this.form.personalCargo,
                    producto: payload.producto,
                    color: this.form.color || 'NO DEFINIDO',
                    medida: this.form.medida || 'NO DEFINIDO',
                    material: OrdenDeProduccion.data.material,
                    observacion: this.form.observaciones,
                    cantidad: this.form.cantidad || 1
                })

                // Si la OP se creó desde una venta, mover el estado a FABRICACIÓN (1)
                // vía orquestador (escribe tm_ventas + historial tb_venta_estados).
                if (ordenPedido) {
                    try {
                        await this.$store.dispatch('inventario/updateEstado', {
                            idVenta: Number(ordenPedido),
                            idEstado: 1,
                            detalle: `OP #${OrdenDeProduccion.data.id} creada`
                        })
                        this.$root.$emit('refresh-reservas')
                    } catch (e) {
                        console.error('Error al actualizar estado a FABRICACIÓN:', e.message)
                    }
                }

                // Auto-fill PROCESO INTERNO en la venta si los campos están vacíos
                // (Se ejecuta al cerrar la orden, no al crear)
                this.loading = false
                this.$router.push(`/logistica/orden-produccion/lista`)
            }
        }
    }
}
</script>
